// Login JWT storage. Stored in a client-side cookie so it can be shared across
// our sub-domains for single sign-on: in production both portals live under
// airporttaxiairportlimo.com, so a cookie scoped to `.airporttaxiairportlimo.com`
// is readable by the booking portal (airporttaxiairportlimo.com) and this
// passenger portal (passenger.airporttaxiairportlimo.com) alike.
//
// On any other host (local dev: *.dev, localhost) we fall back to a host-only
// cookie, so each portal keeps its own login locally. Cross-portal SSO only
// appears once deployed under the shared parent domain (browsers can't share a
// cookie between two different local domains).

const TOKEN_KEY = 'hopn_login_token';
const SHARED_SUFFIX = 'airporttaxiairportlimo.com';
const MAX_AGE = 60 * 60 * 24 * 365; // 1 year — the backend still controls actual
// token validity (expiry / re-issue); this only keeps the cookie around that long.

// localStorage key used purely as a cross-tab signal. The token lives in a cookie,
// and cookie writes do NOT fire `storage` events, so changing this key is how we
// notify other tabs that auth changed (login/logout) — see AuthContext.
export const AUTH_EVENT_KEY = 'hopn_auth_event';

// Ping the signal key so OTHER tabs receive a `storage` event and can re-sync.
function broadcastAuthChange() {
    try {
        localStorage.setItem(AUTH_EVENT_KEY, String(Date.now()));
    } catch { /* localStorage unavailable — ignore */ }
}

// `.airporttaxiairportlimo.com` when we're on the real domain, else null (host-only).
function cookieDomain() {
    return location.hostname.endsWith(SHARED_SUFFIX) ? `.${SHARED_SUFFIX}` : null;
}

export function setToken(token) {
    let cookie = `${TOKEN_KEY}=${encodeURIComponent(token)}; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
    const domain = cookieDomain();
    if (domain) cookie += `; domain=${domain}`;
    if (location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
    broadcastAuthChange();
}

export function getToken() {
    const match = document.cookie.match(/(?:^|;\s*)hopn_login_token=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
}

export function clearToken() {
    // Must match the original domain + path to delete the cookie.
    let cookie = `${TOKEN_KEY}=; path=/; max-age=0; SameSite=Lax`;
    const domain = cookieDomain();
    if (domain) cookie += `; domain=${domain}`;
    if (location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
    broadcastAuthChange();
}
